import React, { useContext, useEffect } from 'react';
import Autosuggest from 'react-autosuggest';
import { Button } from '@patternfly/react-core';
import { TimesIcon } from '@patternfly/react-icons';
import { observer, useLocalStore } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { storesContext } from 'stores';

import './styles.scss';
import Store from './store';

interface SearchAutosuggestProps {
  setMenuOpen?: (value?: boolean) => void;
  autoFocus?: boolean;
}

const SearchAutosuggest = ({
  setMenuOpen = () => {},
  autoFocus = false,
}: SearchAutosuggestProps) => {
  const { searchStore, routerStore, catalogStore, userStore, classesStore } =
    useContext(storesContext);
  const { t } = useTranslation();
  const store = useLocalStore(() => new Store());

  useEffect(() => {
    store.getSavedSearchQuery();
  }, [searchStore.query]);

  const recommendedSearchWithin = 'all';

  const isOnSearcheablePage = () => {
    const routeName = routerStore?.route?.name;
    return routeName === 'search';
  };

  const onSuggestionsFetchRequested = ({ value }: { value: string }) => {
    store.suggestionSelected = false;
    searchStore.query = value;
  };

  const onChange = (_event: any, { newValue }: { newValue: string }) => {
    store.suggestionSelected = false;
    searchStore.query = newValue;
  };

  const onSearch = ({
    query,
    searchWithin,
  }: {
    query: string;
    searchWithin: string;
  }) => {
    if (setMenuOpen && query) {
      setMenuOpen();
    }
    if (!query && !isOnSearcheablePage()) {
      return;
    }

    // trim and replace multiple spaces with a single space
    const trimmedQuery = query.trim().replace(/\s+/g, ' ');
    const newQuery = { q: trimmedQuery, searchWithin, courses: undefined };

    if (userStore.isAlaCarte) {
      newQuery.courses = catalogStore.groupedLegacyOfferings
        .map((e) => e.slug)
        .join(',');
    } else if (userStore.isOpenSubscriber) {
      newQuery.courses = classesStore.getOpenEnrollments().join(',');
    }
    store.saveSearchQuery(trimmedQuery);
    routerStore.navigate('search', newQuery);
  };

  const renderSuggestion = (option: {
    label: string;
    value: { searchWithin: string };
  }) => (
    <div
      className="simple-option"
      style={{ display: 'flex', alignItems: 'center' }}
      data-analytics-id={`search-${option.value.searchWithin}-lp`}
    >
      <div>{option.label}</div>
      <div style={{ marginLeft: 'auto' }}>
        {t('search within')}{' '}
        {t(option.value.searchWithin.split('-').join(' ')).toUpperCase()}
      </div>
    </div>
  );

  const onSuggestionsClearRequested = () => {
    console.warn('onSuggestionsClearRequested');
  };

  const getSuggestionValue = (suggestion: { label: string }) =>
    suggestion.label;

  const onSuggestionSelected = (
    _event: any,
    {
      suggestion,
    }: { suggestion: { value: { query: string; searchWithin: string } } },
  ) => {
    store.suggestionSelected = true;

    onSearch(suggestion.value);
  };

  const clearSearch = () => {
    searchStore.query = '';
    if (isOnSearcheablePage()) {
      onSearch({ query: '', searchWithin: recommendedSearchWithin });
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // handle clicking enter without selecting a suggestion
    if (event.keyCode === 13) {
      if (!store.suggestionSelected) {
        store.suggestionSelected = true;

        onSearch({
          query: searchStore.query,
          searchWithin: recommendedSearchWithin,
        });
      }
    }
  };

  const truncateString = (str: string) => {
    if (str.length > 28) {
      return `${str.slice(0, 25)}...`;
    }
    return str;
  };

  const inputProps = {
    placeholder: t('What are you looking for?'),
    value: searchStore.query,
    onChange,
    onKeyDown,
    'data-analytics-id': 'search-input-lp',
    autoFocus,
    'aria-label': t('Search the catalog for offerings'),
  };

  return (
    <div>
      <div className="search-bar-container">
        <Autosuggest
          suggestions={[]}
          renderSuggestion={renderSuggestion}
          getSuggestionValue={getSuggestionValue}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          inputProps={inputProps}
          onSuggestionSelected={onSuggestionSelected}
        />
        {((routerStore?.route as any)?.params?.q || searchStore.query) && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events
          <span
            tabIndex={0}
            role="button"
            className="clear-button"
            onClick={clearSearch}
            data-analytics-id="search-clear-x-lp"
          >
            <TimesIcon />
          </span>
        )}

        <Button
          variant="danger"
          isDisabled={searchStore.query?.length < 1}
          onClick={() =>
            onSearch({
              query: searchStore.query,
              searchWithin: recommendedSearchWithin,
            })
          }
          className="search-button"
          data-analytics-id="search-btn-header-lp"
        >
          {t('Search')}
        </Button>
      </div>

      <div>
        <ul className="search-suggestions">
          {store.savedSearch.map((item: string) => (
            <li key={item}>
              <Button
                variant="link"
                className="search-suggestions-button"
                onClick={() =>
                  onSearch({
                    query: item,
                    searchWithin: recommendedSearchWithin,
                  })
                }
              >
                {truncateString(item)}
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default observer(SearchAutosuggest);
